import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { getDateString, getMaxMonthDate } from "../../../libs/dateFormat";

import { useUserContext } from "../../../contexts/userContext";

import {
  BuildingFloorItem,
  getBuildingFloorList,
} from "../../../services/buildingServices";
import {
  SpaceReservationItem,
  getSpaceReservationLimitCount,
  getSpaceReservationList,
} from "../../../services/spaceReservationServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonSelect, { SelectOption } from "../../../components/Select";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CustomDatePicker from "../../../components/Input/DatePicker";
import { PostListNoPostIcon } from "../../../components/Talk/PostList";
import FindBuildingPopup from "../../../components/Popup/FindBuilding";
import ReservationLimitPopup from "../../../components/Popup/ReservationLimit";
import SimplePopup from "../../../components/Popup/Simple";
import SpaceReservationListItem from "./ListItem";

import { TalkFindBuildingDownArrow } from "../../Talk";
import { Reason } from "../../../types/location";

type Props = {};

export default function SpaceReservation(props: Props) {
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [floorList, setFloorList] = useState<BuildingFloorItem[]>([]);
  const [limitCount, setLimitCount] = useState<number>(-1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [popup, setPopup] = useState<
    "selectBuilding" | "reservationLimit" | "failedLoadLimitCount" | ""
  >("");
  const reason = Reason.BUILDING_CHANGE;

  const [selectedFloor, setSelectedFloor] = useState<
    BuildingFloorItem | undefined
  >(user.floor);

  const sortOption: SelectOption[] = [
    { label: "높은 층 순", value: "FLOOR_DESC" },
    { label: "낮은 층 순", value: "FLOOR_ASC" },
    { label: "규모 큰 순", value: "AVAILABLE_PEOPLE_DESC" },
    { label: "규모 작은 순", value: "AVAILABLE_PEOPLE_ASC" },
    { label: "최소 예약 시간 적은 순", value: "MIN_RESERVATION_TIME_ASC" },
  ];

  const [sort, setSort] = useState<SelectOption>(sortOption[0]);

  const [spaceReservationList, setSpaceReservationList] = useState<
    SpaceReservationItem[]
  >([]);

  const location = useLocation();

  const [buildingInfo, setBuildingInfo] = useState({
    buildingId: location?.state?.buildingId ?? user.buildingId,
    buildingName: location?.state?.buildingName ?? user.buildingName,
  });

  const maxReservationDate = getMaxMonthDate();

  const loadFloorList = async (
    buildingId?: string,
    buildingFloorId?: string
  ) => {
    const buildingTarget = buildingId ?? buildingInfo.buildingId;
    try {
      if (buildingTarget)
        await getBuildingFloorList({
          buildingId: buildingTarget,
        }).then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setFloorList(res.data);
            if (buildingFloorId) {
              const target = res.data.find(
                (f) => f.buildingFloorId === buildingFloorId
              );
              if (target) setSelectedFloor(target);
            }
          }
        });
    } catch (e) {
      console.log(e);
    }
  };

  const loadSpaceReservationList = () => {
    if (!buildingInfo.buildingId) return;
    setIsLoading(true);

    Promise.allSettled([
      getSpaceReservationList({
        buildingId: buildingInfo.buildingId,
        reservationDate: getDateString(selectedDate),
        buildingFloorId: selectedFloor?.buildingFloorId,
        sort: sort.value,
      }),
      getSpaceReservationLimitCount(),
    ])
      .then((ress) => {
        if (
          ress[0].status === "fulfilled" &&
          ress[0].value.status === HttpStatusCode.Ok
        ) {
          setSpaceReservationList(ress[0].value.data);
        }
        if (
          ress[1].status === "fulfilled" &&
          ress[1].value.status === HttpStatusCode.Ok
        ) {
          setLimitCount(ress[1].value.data.totalConcurrentLimitReservation);
        }
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (location?.state?.buildingFloorId && location?.state?.floorName) {
      loadFloorList(undefined, location?.state?.buildingFloorId);
    }

    if (location?.state?.date) {
      setSelectedDate(location?.state?.date);
    }

    if (location?.state?.sort) {
      const selectedSort = sortOption.find(
        (s) => s.value === location.state.sort
      );
      if (selectedSort) setSort(selectedSort);
    }
    //setIsLoading(false);
  }, [
    location?.state?.buildingFloorId,
    location?.state?.floorName,
    location?.state?.date,
    location?.state?.sort,
  ]);

  useEffect(() => {
    if (location?.state?.buildingId) {
      setBuildingInfo({
        buildingId: location?.state?.buildingId,
        buildingName: location?.state?.buildingName,
      });
    } else {
      setBuildingInfo({
        buildingId: user.buildingId,
        buildingName: user.buildingName,
      });
    }
  }, [
    location?.state?.buildingId,
    location?.state?.buildingName,
    user.buildingId,
    user.buildingName,
  ]);

  useEffect(() => {
    loadFloorList();
  }, [buildingInfo.buildingId, buildingInfo.buildingName]);

  useEffect(() => {
    loadSpaceReservationList();
  }, [
    selectedDate,
    selectedFloor,
    selectedFloor?.buildingFloorId,
    sort,
    user,
    user.building,
    user.isLoading,
    spaceReservationList.length,
    user.buildingId,
    buildingInfo.buildingId,
  ]);

  return (
    <>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader title="공간 예약" onBackClick={() => navigate("/")} />

        <div className="text-3xl font-light py-5 leading-[38px] tracking-[-1.2px]">
          <div className="flex gap-1 items-center">
            <div
              className="flex gap-5 border-b-2 border-black w-fit items-center font-medium"
              onClick={() => {
                setPopup("selectBuilding");
              }}
            >
              <span className=" max-w-[calc(100vw-110px)] truncate">
                {buildingInfo?.buildingName ?? "건물을 선택해주세요"}
              </span>
              <div>
                <TalkFindBuildingDownArrow />
              </div>
            </div>{" "}
            의
          </div>
          <div className="mt-2.5">공간을 예약하시겠어요?</div>
        </div>
        <div className="py-2.5 flex gap-2.5 items-center justify-between flex-wrap">
          <CommonSelect
            defaultValue="층 전체"
            value={selectedFloor?.buildingFloorId.toString() ?? "ALL"}
            optionList={[
              { value: "ALL", label: "층 전체" },
              ...floorList
                .sort((a, b) => {
                  return a.sortOrder < b.sortOrder ? -1 : 1;
                })
                .map((floor) => ({
                  value: floor.buildingFloorId,
                  label: floor.floorName,
                })),
            ]}
            onChange={(floor) => {
              const selectedFloor = floorList.find(
                (f) => f.buildingFloorId === floor.value
              );
              setSelectedFloor(selectedFloor);
              // TODO: 층 선택 시, 정렬 기준 최소 예약 시간 적은 순으로 정렬
            }}
            className="h-bottom-button w-[110px]"
            wrapperClassName="h-bottom-button w-[110px]"
            optionOrder="between"
          />

          <div className="w-[220px]">
            <CustomDatePicker
              date={selectedDate}
              inputType="reservation"
              minDate={new Date()}
              maxDate={maxReservationDate}
              dateFormat="yyyy-MM-dd"
              onChange={(date) => setSelectedDate(date)}
              popperPlacement={"bottom-end"}
              className="w-full"
            />
          </div>
        </div>

        <HorizontalRule margin="0" />

        <div className="pt-5 pb-2.5 justify-end flex">
          <CommonSelect
            value={sort.value}
            optionList={sortOption}
            onChange={(option) => {
              setSort(option);
            }}
            className="border-none max-w-[200px] text-wrap text-right"
            wrapperClassName="flex justify-end"
            optionWidth="200px"
            optionHeight="200px"
            optionOrder="right"
            variant="transparent"
          />
        </div>
        <div className="flex flex-col gap-5" data-testid="reservation-list">
          {spaceReservationList.length === 0 && (
            <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
              <PostListNoPostIcon />
              조회 결과가 없어요
            </div>
          )}
          {spaceReservationList.length > 0 &&
            spaceReservationList.map((spaceReservation) => (
              <SpaceReservationListItem
                key={spaceReservation.reservationSpaceId}
                spaceReservation={spaceReservation}
                onClick={() => {
                  if (limitCount < 0) {
                    setPopup("failedLoadLimitCount");
                    return;
                  }

                  const {
                    isConcurrentLimit,
                    concurrentLimit,
                    isConnected,
                    isParentConcurrentLimit,
                    parentConcurrentLimit,
                  } = spaceReservation;

                  const parentCondition =
                    !isParentConcurrentLimit ||
                    parentConcurrentLimit > limitCount;
                  const defaultCondition =
                    !isConcurrentLimit || concurrentLimit > limitCount;

                  // 회의실이 연결된 경우 부모 회의실의 제한횟수로 적용한다.
                  if (isConnected ? parentCondition : defaultCondition) {
                    navigate(
                      `detail/${
                        spaceReservation.reservationSpaceId
                      }/${getDateString(selectedDate)}`,
                      {
                        state: {
                          parentUrl: "/service/space-reservation",
                          parentState: {
                            buildingId: buildingInfo.buildingId,
                            buildingName: buildingInfo.buildingName,
                            buildingFloorId: selectedFloor?.buildingFloorId,
                            floorName: selectedFloor?.floorName,
                            date: selectedDate,
                            sort: sort.value,
                          },
                        },
                      }
                    );
                  } else {
                    setPopup("reservationLimit");
                  }
                }}
              />
            ))}
        </div>
      </div>

      <FindBuildingPopup
        popup={popup}
        onClose={() => setPopup("")}
        onChangeBuilding={(building) => {
          setBuildingInfo({
            buildingId: building.buildingId,
            buildingName: building.buildingName,
          });
          setSort(sortOption[0]);
          const selectedFloor = floorList.find(
            (f) => f.buildingFloorId === "ALL"
          );
          setSelectedFloor(selectedFloor);
        }}
        type={"SPACE"}
        reason={reason}
      />

      <ReservationLimitPopup popup={popup} onClose={() => setPopup("")} />

      <SimplePopup
        isOpen={popup === "failedLoadLimitCount"}
        onClose={() => {
          setPopup("");
          loadSpaceReservationList();
        }}
        message={
          "공간 정보를 불러오는데 실패했습니다.\n잠시 후 다시 시도해주세요."
        }
      />
    </>
  );
}
